/**
 * ImageUploadSlot Component
 *
 * Upload slot for adding images to a channel.
 * Supports:
 * - Drag & Drop file upload
 * - Click to select file
 * - Gallery selection (existing unused assets)
 *
 * Uses pending upload: Asset is uploaded to S3, but link is created
 * only when user clicks "Speichern".
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Upload, Image as ImageIcon, Loader2 } from 'lucide-react'
import { Button } from '@/components/v2/ui'
import { uploadPendingImage } from '@/lib/api/products'
import { cn } from '@/lib/utils'

export interface PendingUploadResult {
  assetId: string
  contentHash: string
  width: number
  height: number
}

interface ImageUploadSlotProps {
  productId: string
  channelId: string
  channelKey: string
  channelName: string
  onUploadSuccess: (channelId: string, channelKey: string, uploadResult?: PendingUploadResult) => void
  onOpenGallery: () => void
}

export function ImageUploadSlot({
  productId,
  channelId,
  channelKey,
  onUploadSuccess,
  onOpenGallery,
}: ImageUploadSlotProps) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [retryFile, setRetryFile] = useState<File | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Ref for immediate upload lock (useState is async, ref is sync)
  const uploadLockRef = useRef(false)

  const handleFileUpload = useCallback(
    async (file: File) => {
      // Prevent double upload using ref (sync, not affected by React batching)
      if (uploadLockRef.current) {
        console.log('⚠️ Upload already in progress (lock), ignoring')
        return
      }
      uploadLockRef.current = true

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('common.onlyImageFiles'))
        uploadLockRef.current = false
        return
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('common.fileTooLarge'))
        uploadLockRef.current = false
        return
      }

      setUploading(true)
      setLastError(null)
      setRetryFile(null)

      try {
        // Use pending upload - only creates asset, no link
        const result = await uploadPendingImage(productId, file)

        if (result.success) {
          toast.success(t('assets.imageUploadedSave'))
          onUploadSuccess(channelId, channelKey, {
            assetId: result.asset_id,
            contentHash: result.content_hash,
            width: result.width,
            height: result.height,
          })
        } else {
          setLastError(result.error || t('errors.uploadFailed'))
          setRetryFile(file)
          toast.error(result.error || t('errors.uploadFailed'))
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : t('errors.generic')
        setLastError(errorMsg)
        setRetryFile(file)
        toast.error(t('errors.uploadFailedFile', { fileName: errorMsg }))
      } finally {
        setUploading(false)
        uploadLockRef.current = false
      }
    },
    [productId, channelId, channelKey, onUploadSuccess, t]
  )

  const handleRetry = useCallback(() => {
    if (retryFile) {
      handleFileUpload(retryFile)
    }
  }, [retryFile, handleFileUpload])

  const handleClick = useCallback(() => {
    if (!uploading) {
      fileInputRef.current?.click()
    }
  }, [uploading])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        handleFileUpload(file)
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileUpload]
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    setDragging(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    setDragging(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      setDragging(false)

      const file = event.dataTransfer.files[0]
      if (file) {
        handleFileUpload(file)
      }
    },
    [handleFileUpload]
  )

  return (
    <div className="image-upload-slot">
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={uploading}
      />

      {/* Upload Area */}
      <div
        className={cn(
          'relative group rounded-lg border-2 border-dashed',
          'bg-neutral-50 dark:bg-neutral-800 overflow-hidden',
          'transition-colors cursor-pointer',
          'aspect-square',
          dragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
            : 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400 dark:hover:border-primary-500',
          uploading && 'cursor-wait opacity-75'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label={t('assets.uploadImageLabel')}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      >
        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400 mb-2" />
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                {t('common.uploading')}
              </span>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-neutral-400 dark:text-neutral-500 mb-2" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                {t('assets.uploadImageLabel')}
              </span>
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {t('assets.dragDropOrClick')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Gallery Button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full mt-2"
        onClick={(e) => {
          e.stopPropagation()
          onOpenGallery()
        }}
        disabled={uploading}
      >
        <ImageIcon className="h-4 w-4 mr-2" />
        {t('assets.selectFromGallery')}
      </Button>

      {/* Retry Button (bei Fehler) */}
      {lastError && retryFile && (
        <div className="mt-2">
          <div className="text-xs text-red-600 mb-1 truncate" title={lastError}>
            {lastError}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation()
              handleRetry()
            }}
            disabled={uploading}
          >
            {t('common.retry')}
          </Button>
        </div>
      )}
    </div>
  )
}
